import { z } from "zod";
import axios from "axios";
import { signIn } from "next-auth/react";
import { CUSTOMER_USER_ROLE, SALES_AGENT_USER_ROLE } from "@/lib/constants";

const API_URL = process.env.NEXT_PUBLIC_CORE_API_URL;

const signUpWorkerSchema = z.object({
  ippisNumber: z.string(),
  bvn: z.string().min(11, { message: "BVN must be 11 digits" }).max(11, { message: "BVN must be 11 digits" }),
  accountNumber: z
    .string()
    .min(10, { message: "Account number must be 10 digits" })
    .max(10, { message: "Account number must be 10 digits" }),
});

export async function signUpWorker(data: z.infer<typeof signUpWorkerSchema>) {
  const validatedFields = signUpWorkerSchema.safeParse(data);
  if (!validatedFields.success) {
    return { error: validatedFields.error.flatten().fieldErrors };
  }

  const { ippisNumber, bvn, accountNumber } = validatedFields.data;

  try {
    const response = await axios.post(
      `${API_URL}/api/onyx/core/user-onboarding`,
      {
        ippisNumber,
        bvn,
        accountNumber,
      },
      {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      }
    );

    if (!response.data.data) throw new Error("Failed to find your details");

    return {
      email: response.data.data.email,
      firstName: response.data.data.firstName,
      lastName: response.data.data.lastName,
      role: CUSTOMER_USER_ROLE,
    };
  } catch (error) {
    throw error;
  }
}

const confirmPasswordSchema = z
  .object({
    identifier: z.string(),
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .max(16, { message: "Password must be less than 16 characters" })
      .regex(/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,50}$/, {
        message: "Password must contain one number and one special character",
      }),
    confirmPassword: z.string().min(8, { message: "Password must be at least 8 characters" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export async function savePassword(data: z.infer<typeof confirmPasswordSchema>) {
  const validatedFields = confirmPasswordSchema.safeParse(data);
  if (!validatedFields.success) {
    return { error: validatedFields.error.flatten().fieldErrors };
  }

  const { identifier, password, confirmPassword } = validatedFields.data;

  try {
    const response = await axios.post(
      `${API_URL}/api/onyx/core/auth/create-password`,
      {
        identifier,
        password,
        confirmPassword,
      },
      {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      }
    );

    return {
      data: response.data,
    };
  } catch (error) {
    throw error;
  }
}

const loginWorkerSchema = z.object({
  identifier: z.string().min(1, { message: "Identifier is required" }),
  password: z.string(),
  userType: z.enum([CUSTOMER_USER_ROLE, SALES_AGENT_USER_ROLE]),
});

export async function loginWorker(data: z.infer<typeof loginWorkerSchema>) {
  const validatedFields = loginWorkerSchema.safeParse(data);

  if (!validatedFields.success) {
    return { error: "Invalid fields" };
  }

  const { identifier, password, userType } = validatedFields.data;

  try {
    const result = await signIn("credentials", {
      identifier,
      password,
      userType,
      redirect: false,
    });

    if (result?.error) {
      const errorMap: Record<string, string> = {
        CredentialsSignin: "Invalid credentials",
        Configuration: "Invalid credentials",
        Default: "An error occurred while logging in",
      };

      const errorMessage =
        errorMap[result.error] || (typeof result.error === "string" ? result.error : errorMap.Default);

      return { error: errorMessage };
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "An error occurred while logging in";
    return { error: errorMessage };
  }
}

const forgotPasswordSchema = z.object({
  identifier: z.string().min(1, { message: "Identifier is required" }),
});

export async function forgotPassword(data: z.infer<typeof forgotPasswordSchema>) {
  const validatedFields = forgotPasswordSchema.safeParse(data);
  if (!validatedFields.success) {
    return { error: "Invalid fields" };
  }

  const { identifier } = validatedFields.data;

  try {
    const response = await axios.post(`${API_URL}/api/onyx/core/auth/forgot-password`, {
      identifier,
    });
    return {
      message: response.data.message,
    };
  } catch (error) {
    throw error;
  }
}

const verifyOTPSchema = z.object({
  identifier: z.string().min(1, { message: "Identifier is required" }),
  otp: z.string().min(1, { message: "Code is required" }),
});

export async function verifyOTP(data: z.infer<typeof verifyOTPSchema>) {
  const validatedFields = verifyOTPSchema.safeParse(data);
  if (!validatedFields.success) {
    return { error: "Invalid fields" };
  }

  const { identifier, otp } = validatedFields.data;

  try {
    const response = await axios.post(`${API_URL}/api/onyx/core/auth/verify-otp`, {
      identifier,
      otp,
    });
    return {
      message: response.data.message,
      resetToken: response.data.data.resetToken,
    };
  } catch (error) {
    throw error;
  }
}

const resetPasswordSchema = z.object({
  identifier: z.string().min(1, { message: "Identifier is required" }),
  resetToken: z.string().min(1, { message: "Reset token is required" }),
  newPassword: z.string().min(8, { message: "Password must be at least 8 characters" }),
  confirmPassword: z.string().min(8, { message: "Password must be at least 8 characters" }),
});

export async function resetPassword(data: z.infer<typeof resetPasswordSchema>) {
  const validatedFields = resetPasswordSchema.safeParse(data);
  if (!validatedFields.success) {
    return { error: "Invalid fields" };
  }

  const { identifier, resetToken, newPassword, confirmPassword } = validatedFields.data;

  try {
    const response = await axios.post(`${API_URL}/api/onyx/core/auth/update-password`, {
      identifier,
      resetToken,
      newPassword,
      confirmPassword,
    });
    return {
      message: response.data.message,
    };
  } catch (error) {
    return { error: error };
  }
}
